import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ChecklistRtlIcon from "@mui/icons-material/ChecklistRtl";
import DevicesOtherOutlinedIcon from "@mui/icons-material/DevicesOtherOutlined";
import TopNavBar from "../components/TopNavBar";
import DeviceManufacturerStrip from "../components/DeviceManufacturerStrip";
import { getAllTaskMetadata } from "../api/listTypes";
import {
  createServiceTask,
  getServiceTasks,
  updateServiceTask,
  getDeviceManufacturerModels,
  getTaskDeviceBlacklist,
  addTaskDeviceBlacklistEntry,
  removeTaskDeviceBlacklistEntry,
  getAllTaskDeviceBlacklist,
} from "../api/serviceTasks";

const emptyTaskForm = {
  serviceId: "",
  taskName: "",
  taskNameDk: "",
  taskDescription: "",
  taskDescriptionDk: "",
  estimatedDurationMinutes: "",
  active: true,
};

function toEditDraft(task) {
  return {
    serviceId: task.service_id != null ? String(task.service_id) : "",
    taskName: task.task_name || "",
    taskNameDk: task.task_name_dk || "",
    taskDescription: task.task_description || "",
    taskDescriptionDk: task.task_description_dk || "",
    estimatedDurationMinutes:
      task.estimated_duration_minutes != null
        ? String(task.estimated_duration_minutes)
        : "",
    active: task.active !== 0 && task.active !== false,
  };
}

function toApiPayload(draft) {
  return {
    serviceId: Number(draft.serviceId),
    taskName: draft.taskName.trim(),
    taskNameDk: draft.taskNameDk.trim() || null,
    taskDescription: draft.taskDescription.trim() || null,
    taskDescriptionDk: draft.taskDescriptionDk.trim() || null,
    estimatedDurationMinutes: draft.estimatedDurationMinutes
      ? Number(draft.estimatedDurationMinutes)
      : null,
    active: draft.active ? 1 : 0,
  };
}

export default function ConfigureServiceTasksPage() {
  const [tasks, setTasks] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editDraft, setEditDraft] = useState(emptyTaskForm);
  const [savingTaskId, setSavingTaskId] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyTaskForm);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState(null);

  const [manufacturerModels, setManufacturerModels] = useState([]);
  const [allTaskBlacklist, setAllTaskBlacklist] = useState([]);
  const [compatibilityTask, setCompatibilityTask] = useState(null);
  const [compatibilityBlacklist, setCompatibilityBlacklist] = useState([]);
  const [compatibilityLoading, setCompatibilityLoading] = useState(false);
  const [compatibilityTogglingKey, setCompatibilityTogglingKey] =
    useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    Promise.all([
      getServiceTasks(),
      getAllTaskMetadata(),
      getDeviceManufacturerModels(),
      getAllTaskDeviceBlacklist(),
    ])
      .then(([taskData, metadata, manufacturerModelData, blacklistData]) => {
        setTasks(taskData.tasks || []);
        setServices(metadata?.taskMetaData?.services || []);
        setManufacturerModels(manufacturerModelData.manufacturerModels || []);
        setAllTaskBlacklist(blacklistData.blacklist || []);
      })
      .catch((err) => setError(err.message || "Failed to load service tasks"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const groupedManufacturerModels = useMemo(() => {
    const byManufacturer = new Map();
    manufacturerModels.forEach(({ device_manufacturer, device_model }) => {
      if (!byManufacturer.has(device_manufacturer)) {
        byManufacturer.set(device_manufacturer, []);
      }
      if (device_model) {
        byManufacturer.get(device_manufacturer).push(device_model);
      }
    });
    return Array.from(byManufacturer.entries()).map(
      ([manufacturer, models]) => ({ manufacturer, models }),
    );
  }, [manufacturerModels]);

  const allManufacturers = useMemo(
    () => groupedManufacturerModels.map((entry) => entry.manufacturer),
    [groupedManufacturerModels],
  );

  // A manufacturer still "uses" a task unless it's blanket-blacklisted
  // (device_model IS NULL); per-model blacklist entries don't hide it.
  const compatibleManufacturersByTaskId = useMemo(() => {
    const blockedManufacturersByTaskId = new Map();
    allTaskBlacklist.forEach(
      ({ task_id, device_manufacturer, device_model }) => {
        if (device_model != null) return;
        if (!blockedManufacturersByTaskId.has(task_id)) {
          blockedManufacturersByTaskId.set(task_id, new Set());
        }
        blockedManufacturersByTaskId.get(task_id).add(device_manufacturer);
      },
    );

    const result = new Map();
    tasks.forEach((task) => {
      const blocked = blockedManufacturersByTaskId.get(task.pk_id);
      result.set(
        task.pk_id,
        allManufacturers.filter((manufacturer) => !blocked?.has(manufacturer)),
      );
    });
    return result;
  }, [allTaskBlacklist, allManufacturers, tasks]);

  const serviceLabel = (serviceId) => {
    const service = services.find((s) => String(s.id) === String(serviceId));
    return service ? service.name : "-";
  };

  const handleStartEdit = (task) => {
    setEditingTaskId(task.pk_id);
    setEditDraft(toEditDraft(task));
  };

  const handleCancelEdit = () => {
    setEditingTaskId(null);
    setEditDraft(emptyTaskForm);
  };

  const handleSaveEdit = async (taskId) => {
    if (!editDraft.serviceId || !editDraft.taskName.trim()) {
      setSnackbar({
        open: true,
        message: "Service and task name are required",
        severity: "error",
      });
      return;
    }

    setSavingTaskId(taskId);
    try {
      const data = await updateServiceTask(taskId, toApiPayload(editDraft));
      setTasks((prev) => prev.map((t) => (t.pk_id === taskId ? data.task : t)));
      setEditingTaskId(null);
      setSnackbar({ open: true, message: "Task updated", severity: "success" });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Failed to update task",
        severity: "error",
      });
    } finally {
      setSavingTaskId(null);
    }
  };

  const handleOpenCompatibility = async (task) => {
    setCompatibilityTask(task);
    setCompatibilityBlacklist([]);
    setCompatibilityLoading(true);
    try {
      const data = await getTaskDeviceBlacklist(task.pk_id);
      setCompatibilityBlacklist(data.blacklist || []);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Failed to load device compatibility",
        severity: "error",
      });
    } finally {
      setCompatibilityLoading(false);
    }
  };

  const handleCloseCompatibility = () => {
    setCompatibilityTask(null);
    setCompatibilityBlacklist([]);
  };

  const isManufacturerBlacklisted = (manufacturer) =>
    compatibilityBlacklist.some(
      (entry) =>
        entry.device_manufacturer === manufacturer &&
        entry.device_model == null,
    );

  const isModelBlacklisted = (manufacturer, model) =>
    isManufacturerBlacklisted(manufacturer) ||
    compatibilityBlacklist.some(
      (entry) =>
        entry.device_manufacturer === manufacturer &&
        entry.device_model === model,
    );

  const handleToggleCompatibility = async (
    deviceManufacturer,
    deviceModel,
    nextChecked,
  ) => {
    const key = `${deviceManufacturer}::${deviceModel ?? ""}`;
    setCompatibilityTogglingKey(key);
    try {
      const data = nextChecked
        ? await removeTaskDeviceBlacklistEntry(compatibilityTask.pk_id, {
            deviceManufacturer,
            deviceModel,
          })
        : await addTaskDeviceBlacklistEntry(compatibilityTask.pk_id, {
            deviceManufacturer,
            deviceModel,
          });
      setCompatibilityBlacklist(data.blacklist || []);
      setAllTaskBlacklist((prev) => [
        ...prev.filter((entry) => entry.task_id !== compatibilityTask.pk_id),
        ...(data.blacklist || []),
      ]);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Failed to update device compatibility",
        severity: "error",
      });
    } finally {
      setCompatibilityTogglingKey(null);
    }
  };

  const handleOpenCreate = () => {
    setCreateForm(emptyTaskForm);
    setCreateError(null);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!createForm.serviceId || !createForm.taskName.trim()) {
      setCreateError("Service and task name are required");
      return;
    }

    setCreateSaving(true);
    setCreateError(null);
    try {
      const data = await createServiceTask(toApiPayload(createForm));
      setTasks((prev) => [data.task, ...prev]);
      setCreateOpen(false);
      setSnackbar({ open: true, message: "Task created", severity: "success" });
    } catch (err) {
      setCreateError(err.message || "Failed to create service task");
    } finally {
      setCreateSaving(false);
    }
  };

  return (
    <>
      <TopNavBar heading="Configure Service Tasks" />
      <Container maxWidth="lg" sx={{ mt: 3, pb: 4 }}>
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
          <ChecklistRtlIcon sx={{ color: "text.secondary" }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Service Tasks
          </Typography>
          <Box sx={{ ml: "auto" }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
            >
              Create Task
            </Button>
          </Box>
        </Box>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && tasks.length === 0 && (
          <Typography color="text.secondary">
            No service tasks found.
          </Typography>
        )}

        {!loading && !error && tasks.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Id</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Task Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    Task Description UK/DK
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Service</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    Duration (min)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((task, index) => {
                  const isEditing = editingTaskId === task.pk_id;
                  const isSaving = savingTaskId === task.pk_id;

                  if (isEditing) {
                    return (
                      <TableRow key={task.pk_id} hover>
                        {/* ID — keep in the same column as the normal row */}
                        <TableCell sx={{ verticalAlign: "top" }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            #{index + 1}. Id.{task.pk_id}
                          </Typography>
                        </TableCell>

                        {/* Task Name — English and Danish */}
                        <TableCell sx={{ verticalAlign: "top", minWidth: 200 }}>
                          <Stack spacing={1}>
                            <TextField
                              size="small"
                              fullWidth
                              label="Task Name"
                              value={editDraft.taskName}
                              onChange={(e) =>
                                setEditDraft((prev) => ({
                                  ...prev,
                                  taskName: e.target.value,
                                }))
                              }
                            />

                            <TextField
                              size="small"
                              fullWidth
                              label="Task Name (DK)"
                              value={editDraft.taskNameDk}
                              onChange={(e) =>
                                setEditDraft((prev) => ({
                                  ...prev,
                                  taskNameDk: e.target.value,
                                }))
                              }
                            />
                          </Stack>
                        </TableCell>

                        {/* Descriptions — separate English and Danish fields */}
                        <TableCell sx={{ verticalAlign: "top", minWidth: 280 }}>
                          <Stack spacing={1}>
                            <TextField
                              size="small"
                              fullWidth
                              multiline
                              minRows={2}
                              label="Description (EN)"
                              value={editDraft.taskDescription}
                              onChange={(e) =>
                                setEditDraft((prev) => ({
                                  ...prev,
                                  taskDescription: e.target.value,
                                }))
                              }
                            />

                            <TextField
                              size="small"
                              fullWidth
                              multiline
                              minRows={2}
                              label="Description (DK)"
                              value={editDraft.taskDescriptionDk}
                              onChange={(e) =>
                                setEditDraft((prev) => ({
                                  ...prev,
                                  taskDescriptionDk: e.target.value,
                                }))
                              }
                            />
                          </Stack>
                        </TableCell>

                        {/* Service */}
                        <TableCell sx={{ verticalAlign: "top", minWidth: 160 }}>
                          <TextField
                            select
                            size="small"
                            fullWidth
                            label="Service"
                            value={editDraft.serviceId}
                            onChange={(e) =>
                              setEditDraft((prev) => ({
                                ...prev,
                                serviceId: e.target.value,
                              }))
                            }
                          >
                            {services.map((service) => (
                              <MenuItem
                                key={service.id}
                                value={String(service.id)}
                              >
                                {service.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>

                        {/* Duration */}
                        <TableCell align="center" sx={{ verticalAlign: "top" }}>
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{
                              min: 0,
                              step: 0.25,
                              style: { textAlign: "center" },
                            }}
                            sx={{ minWidth: 85 }}
                            value={editDraft.estimatedDurationMinutes}
                            onChange={(e) =>
                              setEditDraft((prev) => ({
                                ...prev,
                                estimatedDurationMinutes: e.target.value,
                              }))
                            }
                          />
                        </TableCell>

                        {/* Status */}
                        <TableCell align="center" sx={{ verticalAlign: "top" }}>
                          <Switch
                            checked={editDraft.active}
                            onChange={(e) =>
                              setEditDraft((prev) => ({
                                ...prev,
                                active: e.target.checked,
                              }))
                            }
                          />
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="right" sx={{ verticalAlign: "top" }}>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="flex-end"
                          >
                            <Tooltip title="Save">
                              <span>
                                <IconButton
                                  size="small"
                                  color="success"
                                  disabled={isSaving}
                                  onClick={() => handleSaveEdit(task.pk_id)}
                                >
                                  {isSaving ? (
                                    <CircularProgress size={18} />
                                  ) : (
                                    <SaveOutlinedIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title="Cancel">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={isSaving}
                                  onClick={handleCancelEdit}
                                >
                                  <CloseOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return (
                    <TableRow key={task.pk_id} hover>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          #{index + 1}. Id.{task.pk_id}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {task.task_name}
                        </Typography>

                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {task.task_name_dk}
                        </Typography>

                        <DeviceManufacturerStrip
                          manufacturers={
                            compatibleManufacturersByTaskId.get(task.pk_id) ||
                            []
                          }
                          sx={{ mt: 0.5 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            paddingBottom: 2,
                            fontWeight: 500,
                          }}
                        >
                          {task.task_description || "-"}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {task.task_description_dk || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        {task.service_name || serviceLabel(task.service_id)}
                      </TableCell>
                      <TableCell align="center" sx={{ verticalAlign: "top" }}>
                        {task.estimated_duration_minutes ?? "-"}
                      </TableCell>
                      <TableCell align="center" sx={{ verticalAlign: "top" }}>
                        <Chip
                          label={task.active ? "Active" : "Inactive"}
                          size="small"
                          color={task.active ? "success" : "default"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ verticalAlign: "top" }}>
                        <Tooltip title="Device compatibility">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenCompatibility(task)}
                          >
                            <DevicesOtherOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleStartEdit(task)}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Container>

      <Dialog
        open={createOpen}
        onClose={() => {
          if (!createSaving) setCreateOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create Service Task</DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {createError && <Alert severity="error">{createError}</Alert>}

            <TextField
              select
              label="Service"
              fullWidth
              value={createForm.serviceId}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  serviceId: e.target.value,
                }))
              }
            >
              <MenuItem value="">Select Service</MenuItem>
              {services.map((service) => (
                <MenuItem key={service.id} value={String(service.id)}>
                  {service.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Task Name"
              fullWidth
              value={createForm.taskName}
              onChange={(e) =>
                setCreateForm((prev) => ({ ...prev, taskName: e.target.value }))
              }
            />

            <TextField
              label="Task Name (DK)"
              fullWidth
              value={createForm.taskNameDk}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  taskNameDk: e.target.value,
                }))
              }
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              value={createForm.taskDescription}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  taskDescription: e.target.value,
                }))
              }
            />

            <TextField
              label="Description (DK)"
              fullWidth
              multiline
              minRows={2}
              value={createForm.taskDescriptionDk}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  taskDescriptionDk: e.target.value,
                }))
              }
            />

            <TextField
              label="Estimated Duration (minutes)"
              type="number"
              inputProps={{ min: 0 }}
              value={createForm.estimatedDurationMinutes}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  estimatedDurationMinutes: e.target.value,
                }))
              }
            />

            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={createForm.active}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    active: e.target.checked,
                  }))
                }
              />
              <Typography variant="body2">Active</Typography>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCreateOpen(false)}
            disabled={createSaving}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={createSaving}
          >
            {createSaving ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(compatibilityTask)}
        onClose={handleCloseCompatibility}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          Device Compatibility
          {compatibilityTask ? ` — ${compatibilityTask.task_name}` : ""}
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Checked manufacturers/models can use this task. Unchecking adds them
            to the task's device blacklist.
          </Typography>

          {compatibilityLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {groupedManufacturerModels.map(({ manufacturer, models }) => {
                const manufacturerChecked =
                  !isManufacturerBlacklisted(manufacturer);
                const manufacturerKey = `${manufacturer}::`;

                return (
                  <Box key={manufacturer}>
                    <FormControlLabel
                      label={
                        <Typography sx={{ fontWeight: 600 }}>
                          {manufacturer}
                        </Typography>
                      }
                      control={
                        <Checkbox
                          size="small"
                          checked={manufacturerChecked}
                          disabled={
                            compatibilityTogglingKey === manufacturerKey
                          }
                          onChange={() =>
                            handleToggleCompatibility(
                              manufacturer,
                              null,
                              !manufacturerChecked,
                            )
                          }
                        />
                      }
                    />
                    {models.length > 0 && (
                      <FormGroup sx={{ pl: 3 }}>
                        {models.map((model) => {
                          const modelChecked = !isModelBlacklisted(
                            manufacturer,
                            model,
                          );
                          const modelKey = `${manufacturer}::${model}`;

                          return (
                            <FormControlLabel
                              key={model}
                              label={model}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={modelChecked}
                                  disabled={
                                    !manufacturerChecked ||
                                    compatibilityTogglingKey === modelKey
                                  }
                                  onChange={() =>
                                    handleToggleCompatibility(
                                      manufacturer,
                                      model,
                                      !modelChecked,
                                    )
                                  }
                                />
                              }
                            />
                          );
                        })}
                      </FormGroup>
                    )}
                  </Box>
                );
              })}
              {groupedManufacturerModels.length === 0 && (
                <Typography color="text.secondary" variant="body2">
                  No devices found to list manufacturers/models from.
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseCompatibility}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
